import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  FICO_BANDS,
  ENTITY_TYPES,
  ADDRESS_TYPES,
  PREFERRED_CHANNELS,
  submitCreditReadiness,
} from "../lib/creditReadiness";
import { formatApiErrorDetail } from "../lib/api";

const SECTIONS = ["applicant", "business", "credit", "goals"];

const INIT = {
  full_name: "",
  email: "",
  phone: "",
  business_name: "",
  entity_type: "LLC",
  state_of_formation: "",
  naics_code: "",
  industry_description: "",
  years_in_business: 0,
  annual_revenue_usd: 0,
  monthly_cash_flow_usd: 0,
  personal_fico_band: "660-699",
  has_duns: false,
  duns_number: "",
  paydex_score: "",
  existing_tradelines: 0,
  business_bank_account: false,
  business_address_type: "residential",
  has_800_phone: false,
  on_sos_good_standing: true,
  recent_derogatories: false,
  target_funding_amount_usd: 80000,
  funding_purpose: "",
  preferred_channels: [],
  notes: "",
};

export default function CreditReadinessApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INIT);
  const [section, setSection] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (id) =>
    setForm((f) => ({
      ...f,
      preferred_channels: f.preferred_channels.includes(id)
        ? f.preferred_channels.filter((c) => c !== id)
        : [...f.preferred_channels, id],
    }));

  const next = (e) => {
    e?.preventDefault?.();
    if (section < SECTIONS.length - 1) {
      setSection((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const back = () => {
    if (section > 0) setSection((s) => s - 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        years_in_business: Number(form.years_in_business) || 0,
        annual_revenue_usd: Number(form.annual_revenue_usd) || 0,
        monthly_cash_flow_usd: Number(form.monthly_cash_flow_usd) || 0,
        existing_tradelines: Number(form.existing_tradelines) || 0,
        target_funding_amount_usd: Number(form.target_funding_amount_usd) || 0,
        paydex_score: form.paydex_score === "" || form.paydex_score === null ? null : Number(form.paydex_score),
      };
      const rec = await submitCreditReadiness(payload);
      setDone(rec);
      toast.success("Diagnostic queued. WealthFirst FundingBot is analyzing your profile.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="cr-applied" className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
        <CheckCircle2 size={56} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
        <p className="eyebrow mb-4">Diagnostic Queued</p>
        <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
          WealthFirst FundingBot is running.
        </h1>
        <p className="text-base leading-relaxed mb-10" style={{ color: "var(--ink-muted)" }}>
          Your personalized Business Credit Readiness Report is generating now. It usually takes under two minutes.
          You will be redirected as soon as it is ready.
        </p>
        <button
          type="button"
          className="btn-brass"
          onClick={() => navigate(`/credit-readiness/report/${done.id}`)}
          data-testid="cr-go-report"
        >
          Open my report <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="cr-apply-page" className="max-w-3xl mx-auto px-6 pt-32 pb-24">
      <p className="eyebrow mb-4">WealthFirst AI Diagnostic</p>
      <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
        Tell us about your <em style={{ color: "var(--brass)" }}>business.</em>
      </h1>
      <p className="text-sm mb-10" style={{ color: "var(--ink-muted)" }}>
        Step {section + 1} of {SECTIONS.length} · This is a diagnostic, not a credit pull. Your information is encrypted in
        transit and used only to generate your personalized funding strategy.
      </p>

      <div className="flex gap-2 mb-10" data-testid="cr-progress">
        {SECTIONS.map((_, i) => (
          <div
            key={i}
            className="flex-1 h-1"
            style={{ background: i <= section ? "var(--brass)" : "var(--hairline-strong)" }}
          />
        ))}
      </div>

      <form onSubmit={section === SECTIONS.length - 1 ? submit : next} className="space-y-6">
        {/* SECTION 0 — APPLICANT */}
        {section === 0 && (
          <div className="space-y-6" data-testid="cr-section-applicant">
            <Field label="Full Name *">
              <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="input-line" data-testid="cr-full-name" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email *">
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" data-testid="cr-email" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-line" data-testid="cr-phone" />
              </Field>
            </div>
          </div>
        )}

        {/* SECTION 1 — BUSINESS */}
        {section === 1 && (
          <div className="space-y-6" data-testid="cr-section-business">
            <Field label="Business Name *">
              <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="input-line" data-testid="cr-business-name" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Entity Type *">
                <Select value={form.entity_type} onChange={(v) => set("entity_type", v)} options={ENTITY_TYPES} testid="cr-entity-type" />
              </Field>
              <Field label="State of Formation *">
                <input required value={form.state_of_formation} onChange={(e) => set("state_of_formation", e.target.value)} placeholder="e.g. Wyoming" className="input-line" data-testid="cr-state" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="NAICS Code (6 digits)">
                <input value={form.naics_code} onChange={(e) => set("naics_code", e.target.value)} placeholder="541512" className="input-line" data-testid="cr-naics" />
              </Field>
              <Field label="Years in Business">
                <input type="number" step="0.1" min="0" value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} className="input-line" data-testid="cr-years" />
              </Field>
            </div>
            <Field label="Industry Description">
              <textarea rows={3} value={form.industry_description} onChange={(e) => set("industry_description", e.target.value)} placeholder="What does your business do?" className="w-full bg-transparent p-3 text-base outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="cr-industry" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Annual Revenue (USD)">
                <input type="number" min="0" value={form.annual_revenue_usd} onChange={(e) => set("annual_revenue_usd", e.target.value)} className="input-line" data-testid="cr-revenue" />
              </Field>
              <Field label="Monthly Cash Flow (USD)">
                <input type="number" value={form.monthly_cash_flow_usd} onChange={(e) => set("monthly_cash_flow_usd", e.target.value)} className="input-line" data-testid="cr-cashflow" />
              </Field>
            </div>
          </div>
        )}

        {/* SECTION 2 — CREDIT POSTURE */}
        {section === 2 && (
          <div className="space-y-6" data-testid="cr-section-credit">
            <Field label="Personal FICO Band *">
              <Select value={form.personal_fico_band} onChange={(v) => set("personal_fico_band", v)} options={FICO_BANDS} testid="cr-fico" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="DUNS Number (if any)">
                <input value={form.duns_number} onChange={(e) => { set("duns_number", e.target.value); set("has_duns", !!e.target.value); }} className="input-line" data-testid="cr-duns" />
              </Field>
              <Field label="D&B Paydex Score (0-100, if known)">
                <input type="number" min="0" max="100" value={form.paydex_score} onChange={(e) => set("paydex_score", e.target.value)} className="input-line" data-testid="cr-paydex" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Existing Reporting Tradelines">
                <input type="number" min="0" value={form.existing_tradelines} onChange={(e) => set("existing_tradelines", e.target.value)} className="input-line" data-testid="cr-tradelines" />
              </Field>
              <Field label="Registered Address Type">
                <Select value={form.business_address_type} onChange={(v) => set("business_address_type", v)} options={ADDRESS_TYPES.map((a) => a.id)} display={(id) => ADDRESS_TYPES.find((a) => a.id === id)?.label || id} testid="cr-address-type" />
              </Field>
            </div>
            <div className="space-y-3">
              <Checkbox label="I have a business bank account (≥ 90-day seasoning)" checked={form.business_bank_account} onChange={(v) => set("business_bank_account", v)} testid="cr-bank-account" />
              <Checkbox label="I have a dedicated 800 / toll-free phone number" checked={form.has_800_phone} onChange={(v) => set("has_800_phone", v)} testid="cr-800-phone" />
              <Checkbox label="My business is in Sec-of-State good standing" checked={form.on_sos_good_standing} onChange={(v) => set("on_sos_good_standing", v)} testid="cr-sos" />
              <Checkbox label="I have recent personal credit derogatories (last 24 mo)" checked={form.recent_derogatories} onChange={(v) => set("recent_derogatories", v)} testid="cr-derog" />
            </div>
          </div>
        )}

        {/* SECTION 3 — GOALS */}
        {section === 3 && (
          <div className="space-y-6" data-testid="cr-section-goals">
            <Field label="Target Funding Amount (USD) *">
              <input required type="number" min="1000" value={form.target_funding_amount_usd} onChange={(e) => set("target_funding_amount_usd", e.target.value)} className="input-line" data-testid="cr-target-funding" />
            </Field>
            <Field label="Funding Purpose *">
              <textarea required rows={3} value={form.funding_purpose} onChange={(e) => set("funding_purpose", e.target.value)} placeholder="e.g. Inventory expansion, payroll runway, real estate acquisition…" className="w-full bg-transparent p-3 text-base outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="cr-purpose" />
            </Field>
            <Field label="Preferred Funding Channels (multi-select)">
              <div className="flex flex-wrap gap-2 mt-2" data-testid="cr-channels">
                {PREFERRED_CHANNELS.map((c) => {
                  const active = form.preferred_channels.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleChannel(c.id)}
                      data-testid={`cr-channel-${c.id}`}
                      className="text-xs uppercase tracking-[0.12em] px-3 py-1.5 transition-colors"
                      style={{
                        border: `1px solid ${active ? "var(--brass)" : "var(--hairline-strong)"}`,
                        background: active ? "rgba(194,163,115,0.1)" : "transparent",
                        color: active ? "var(--brass)" : "var(--ink-muted)",
                      }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Notes (optional)">
              <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full bg-transparent p-3 text-base outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="cr-notes" />
            </Field>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          {section > 0 && (
            <button type="button" onClick={back} className="px-6 py-3 text-sm uppercase tracking-[0.18em]" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }} data-testid="cr-back">
              ← Back
            </button>
          )}
          <button type="submit" disabled={busy} className="btn-brass flex-1 justify-center" data-testid="cr-submit">
            {busy
              ? "Submitting…"
              : section === SECTIONS.length - 1
                ? "Run WealthFirst FundingBot →"
                : "Continue →"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, display, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input-line"
      style={{ background: "var(--ink)", color: "var(--ink-light)" }}
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {display ? display(o) : o}
        </option>
      ))}
    </select>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        style={{ accentColor: "var(--brass)", width: 18, height: 18 }}
      />
      <span className="text-sm" style={{ color: "var(--ink)" }}>{label}</span>
    </label>
  );
}
